import { msg } from "@lingui/core/macro";
import { type JSX, Show } from "solid-js";
import { useLocation } from "@tanstack/solid-router";
import { useNavigate } from "@/lib/u-router";
import { safeRedirectTarget, useSession } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LogIn, Sparkles } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";

interface AuthPlaceholderProps {
  title: string;
  description: string;
  children: JSX.Element;
}

export function AuthPlaceholder(props: AuthPlaceholderProps) {
  const { t } = useI18n();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Show
      when={session.isAuthenticated()}
      fallback={
        <div class="relative flex-1 p-6 md:p-10">
          {/* Faded Background Content */}
          <div class="pointer-events-none opacity-20 blur-[2px]">{props.children}</div>

          {/* Centered Overlay */}
          <div class="absolute inset-0 flex items-center justify-center p-6 bg-background/40 backdrop-blur-[1px]">
            <Card class="w-full max-w-md border-primary/20 shadow-2xl animate-in fade-in zoom-in duration-300">
              <CardHeader class="flex flex-col items-center text-center space-y-4">
                <div class="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary rotate-3 transition-transform hover:rotate-0">
                  <Sparkles class="h-8 w-8" />
                </div>
                <div class="space-y-2">
                  <CardTitle class="text-2xl font-bold tracking-tight">{props.title}</CardTitle>
                  <p class="text-muted-foreground leading-relaxed">{props.description}</p>
                </div>
              </CardHeader>
              <CardContent class="flex flex-col gap-3">
                <Button
                  size="lg"
                  onClick={() =>
                    void navigate({
                      to: "/login",
                      search: { redirect: safeRedirectTarget(location().pathname) },
                    })
                  }
                  class="w-full gap-2 font-semibold shadow-lg shadow-primary/20"
                >
                  <LogIn class="h-4 w-4" />
                  {t(msg`Sign in now`)}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void navigate({ to: "/" })}
                  class="text-xs text-muted-foreground hover:text-primary"
                >
                  {t(msg`Back to Dashboard`)}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      }
    >
      {props.children}
    </Show>
  );
}
